import {
	ArrayMaxSize,
	ArrayMinSize,
	IsArray,
	IsNotEmpty,
	IsOptional,
	ValidateIf,
	ValidateNested,
} from 'class-validator';
import { Transform, Type } from 'class-transformer';
import { PrivacyTypesEnum, UserToEntityRelationVisibilityEnum } from '../../enums';
import { InterestDto } from './interest.dto';
import { EventDto, PrivacyTypeDto } from './event.dto';
import { FileDto, FileUploadDto } from './file.dto';
import { ALLOWED_PAST_EVENT_IMAGE_MIME_TYPES, ALLOWED_PAST_EVENT_VIDEO_MIME_TYPES } from '../../constants';
import { Base64AllowedMimeTypes, MaxBase64FileSize } from '../../decorators';
import { UserDto } from './user.dto';
import { PaginationFilter } from '../../interfaces/filters/pagination.filter';
import { OrderFilter } from '../../interfaces/filters/order.filter';
import { OrderParamPair } from '../../interfaces/filters/order-param-pair';
import { DateFilter } from '../../interfaces/filters/date.filter';
import { ReactionToUserToPastEventDto } from './reaction-to-user-to-past-event.dto';
import { UsersToPastEventsFilter } from '../filters';
import { ReactionTypeWithTotalCountDto } from './reaction.dto';
import { dateStringToDate } from '../../types-transformers/date-transformers';
import { stringToBoolean } from '../../types-transformers/boolean-transformers';
import { OrderDto } from './order.dto';
import { ShortActivityDto } from './feed.dto';
import { ShortFitnessTypeDto } from './event-to-fitness-type.dto';

export class PastEventAdditionalDataDto {
	fitnessTypes?: ShortFitnessTypeDto[];
}

export class UserEventsJournal {
	public proposedEvents: PastEventDto[];
	public events: PastEventDto[];
}

export class UserToPastEventDto {
	public id: string;
	public userId: string;
	public pastEventId: string;
	public eventVisibilityId: UserToEntityRelationVisibilityEnum;
	public participationPrivacyId: PrivacyTypesEnum;
	public comment?: string;
	public isDraft: boolean;
	public isWorkout: boolean;
	public seenAt?: Date;
	public weekIndex?: number;
	public monthIndex?: number;
	public overallIndex?: number;
	public createdAt: Date;
	public updatedAt: Date;
	public feedSyncedAt?: Date;
	public feedData?: ShortActivityDto;

	public user?: UserDto;
	public pastEvent?: PastEventDto;
	public participationPrivacy?: PrivacyTypesEnum;
	public files?: PastEventFileDto[];
	public filesTotalCount?: number;
	public currentUserReaction?: ReactionToUserToPastEventDto;
	public reactionTypes?: ReactionTypeWithTotalCountDto[];
}

export class PastEventDto {
	public id: string;
	public name: string;
	public isPaid: boolean;
	public isDonationBased: boolean;
	public addressLat: string;
	public addressLng: string;
	public addressName: string;
	public isOnlineEvent: boolean;
	public onlineUrl: string;
	public countryId: string;
	public stateId: string;
	public cityId: string;
	public fitnessTypeId: string;
	public fitnessType?: InterestDto;
	public masteryLevel: number;
	public privacyId: PrivacyTypesEnum;
	public privacy?: PrivacyTypeDto;
	public description?: string;
	public baseEventId: string;
	public primaryEventId: string;
	public event?: EventDto;

	public eventAdditionalData: PastEventAdditionalDataDto;

	public startedAt: Date;
	public endDate: Date;
	public isEnded: boolean;

	public createdAt: Date;
	public updatedAt?: Date | null;

	public attendees?: UserToPastEventDto[];
	public attendeesTotalCount?: number;
	public currentUserToPastEvent?: UserToPastEventDto;
	public eventPayments?: OrderDto[];
	public revenue?: number;
}

export class PastEventFilesFilterDto implements PaginationFilter, OrderFilter<PastEventFileDto>, DateFilter {
	authorName?: string;
	authorsIds?: string[];
	mimeTypes?: string[];
	fileName?: string;

	periodFrom?: Date;
	periodTo?: Date;

	/**
	 * When true, shows all entries without privacy filtering (only for Admins)
	 */
	@Transform(stringToBoolean)
	public ignorePrivacyFiltering?: boolean;

	pageSize?: number | null;
	page?: number | null;
	orderedBy?: OrderParamPair<PastEventFileDto>[] | null;
}

export class PastEventFilesUploadDto {
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	@ArrayMinSize(0)
	@ArrayMaxSize(10)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_PAST_EVENT_IMAGE_MIME_TYPES)
	public imagesToUpload: FileUploadDto[];

	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	@ArrayMinSize(0)
	@ArrayMaxSize(10)
	@MaxBase64FileSize(100)
	@Base64AllowedMimeTypes(ALLOWED_PAST_EVENT_VIDEO_MIME_TYPES)
	public videosToUpload: FileUploadDto[];
}

export class PastEventFileDto {
	public id: string;
	public fileId: string;
	public authorId: string;
	public pastEventId: string;
	public createdAt: Date;
	public updatedAt?: Date | null;
	public file: FileDto;
}

export class ModifyUserToPastEventDto {
	/**
	 * Comment to attach to user's interaction with past event
	 */
	public comment?: string;
	/**
	 * Privacy settings for content a user attached
	 */
	public participationPrivacyId?: PrivacyTypesEnum;

	public isWorkout?: boolean;
}

export class ModifyUserToPastEventWithFilesDto extends ModifyUserToPastEventDto {
	/**
	 * IDs of files, that should be deleted
	 */
	@IsOptional()
	@IsArray()
	public fileIdsToDelete?: string[];

	/**
	 * Files, that should be added when event is added to the journal
	 */
	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	@ArrayMinSize(0)
	@ArrayMaxSize(10)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_PAST_EVENT_IMAGE_MIME_TYPES)
	public imagesToUpload?: FileUploadDto[];

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	@ArrayMinSize(0)
	@ArrayMaxSize(10)
	@MaxBase64FileSize(100)
	@Base64AllowedMimeTypes(ALLOWED_PAST_EVENT_VIDEO_MIME_TYPES)
	public videosToUpload: FileUploadDto[];
}

export class CreateUserToPastEventDto extends ModifyUserToPastEventDto {
	/**
	 * Files, that should be added when event is added to the journal
	 */
	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	@ArrayMinSize(0)
	@ArrayMaxSize(10)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_PAST_EVENT_IMAGE_MIME_TYPES)
	public imagesToUpload?: FileUploadDto[];

	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	@ArrayMinSize(0)
	@ArrayMaxSize(10)
	@MaxBase64FileSize(100)
	@Base64AllowedMimeTypes(ALLOWED_PAST_EVENT_VIDEO_MIME_TYPES)
	public videosToUpload: FileUploadDto[];
}

export class CreatePastEventFromAdminPanelDto implements PastEventFilesUploadDto, CreateUserToPastEventDto {
	public type?: 'from-scratch' | 'event-based';

	@IsOptional()
	@ValidateIf(({ type, name }) => name !== '' && type === 'from-scratch')
	@IsNotEmpty()
	public name?: string;

	@ValidateIf(({ type }) => type === 'from-scratch')
	@IsNotEmpty()
	public fitnessTypeId?: string;

	@ValidateIf(({ type }) => type === 'from-scratch')
	@IsOptional()
	@IsNotEmpty()
	public masteryLevel?: number;

	@ValidateIf(({ type }) => type === 'from-scratch')
	@IsNotEmpty()
	public startedAt?: Date;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	@ArrayMinSize(0)
	@ArrayMaxSize(10)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_PAST_EVENT_IMAGE_MIME_TYPES)
	public imagesToUpload: FileUploadDto[];

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	@ArrayMinSize(0)
	@ArrayMaxSize(10)
	@MaxBase64FileSize(100)
	@Base64AllowedMimeTypes(ALLOWED_PAST_EVENT_VIDEO_MIME_TYPES)
	public videosToUpload: FileUploadDto[];

	public fileIdsToDelete?: string[];

	@ValidateIf(({ type }) => type === 'event-based')
	@IsNotEmpty()
	public pastEventId?: string;

	@IsNotEmpty()
	public userId?: string;

	public participationPrivacyId: PrivacyTypesEnum;

	public comment?: string;

	public isWorkout?: boolean;

	public feedSyncedAt?: Date;

	public eventAdditionalData?: PastEventAdditionalDataDto;

	public static fromUserToPastEvent(userToPastEvent?: UserToPastEventDto): CreatePastEventFromAdminPanelDto {
		const journal = new CreatePastEventFromAdminPanelDto();

		journal.type = userToPastEvent?.pastEvent?.baseEventId ? 'event-based' : 'from-scratch';
		journal.pastEventId = userToPastEvent?.pastEvent?.id;
		journal.fitnessTypeId = userToPastEvent?.pastEvent?.fitnessTypeId;
		journal.imagesToUpload = [];
		journal.videosToUpload = [];
		journal.fileIdsToDelete = [];
		journal.userId = userToPastEvent?.userId;
		journal.participationPrivacyId = userToPastEvent?.participationPrivacyId ?? PrivacyTypesEnum.Public;
		journal.comment = userToPastEvent?.comment ?? '';
		journal.isWorkout = userToPastEvent?.isWorkout ?? true;

		if (journal.type === 'from-scratch') {
			journal.name = userToPastEvent?.pastEvent?.name ?? '';
			journal.startedAt = userToPastEvent?.pastEvent?.startedAt;
		}

		return journal;
	}
}

export class CreatePastEventDto implements PastEventFilesUploadDto, CreateUserToPastEventDto {
	public name: string;
	public addressLat: string;
	public addressLng: string;
	public addressName: string;
	public startedAt: Date;
	public fitnessTypeId?: string;
	public masteryLevel?: number | null;

	public participationPrivacyId: PrivacyTypesEnum;
	public comment?: string;
	public isWorkout?: boolean;

	public eventAdditionalData?: PastEventAdditionalDataDto;

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	@ArrayMinSize(0)
	@ArrayMaxSize(10)
	@MaxBase64FileSize(20)
	@Base64AllowedMimeTypes(ALLOWED_PAST_EVENT_IMAGE_MIME_TYPES)
	public imagesToUpload: FileUploadDto[];

	@IsOptional()
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => FileUploadDto)
	@ArrayMinSize(0)
	@ArrayMaxSize(10)
	@MaxBase64FileSize(100)
	@Base64AllowedMimeTypes(ALLOWED_PAST_EVENT_VIDEO_MIME_TYPES)
	public videosToUpload: FileUploadDto[];
}

export interface PastEventsWithGroupedDraftsDto {
	usersToPastEvents: UserToPastEventDto[];
	groupedDraftUsersToPastEvents?: PastEventDto[];
	nextFilter: UsersToPastEventsFilter | null;
}

export class MarkPastEventsAsSeenDto {
	@Transform(dateStringToDate)
	public date: Date;
}
